import React, { ReactElement, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Modal,
  Select,
  SpaceBetween,
  Spinner,
  TextContent,
} from '@amzn/awsui-components-react-v3';
import { OptionDefinition } from '@amzn/awsui-components-react-v3/polaris/internal/components/option/interfaces';
import { useMutation } from '@apollo/client';
import logger from '@amzn/awsui-components-react/polaris/internal/core/utils/src/logger';
import { useTranslation } from 'react-i18next';
import { NonCancelableEventHandler } from '@amzn/awsui-components-react-v3/polaris/internal/events';
import { DeleteContainerRequest } from '../../../services/container-management/types';
import { DELETE_CONTAINER } from '../../../services/container-management/queries';

export type ContainerDeleteConfirmationModalProps = {
  containerId: string;
  containerName: string;
  version: number;
  deleteReasonCodes: OptionDefinition[];
  showContainerDeleteConfirmModal: boolean;
  onClose: NonCancelableEventHandler;
};

const ContainerDeleteConfirmationModal = (
  data: ContainerDeleteConfirmationModalProps
): ReactElement => {
  const { t } = useTranslation();
  const [deleteReasonCode, setDeleteReasonCode] = useState<OptionDefinition | null>(null);
  const [deleteContainerSuccessResponse, setDeleteContainerSuccessResponse] = useState<string>('');

  const [
    deleteContainer,
    { loading: deleteContainerLoading, error: deleteContainerError },
  ] = useMutation(DELETE_CONTAINER, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    onCompleted: (result) => {
      setDeleteContainerSuccessResponse(result.deleteContainer.containerId);
    },
  });

  const callDeleteContainer = (request: DeleteContainerRequest): void => {
    deleteContainer({
      variables: {
        ...request,
      },
    }).catch((error) => {
      logger.warn(`Error when deleting container: ${error}`);
    });
  };

  const handleDeleteContainer = () => {
    const request: DeleteContainerRequest = {
      containerId: data.containerId,
      version: data.version,
      reasonCode: Number(deleteReasonCode?.value),
    };
    callDeleteContainer(request);
  };

  if (deleteContainerLoading) {
    return (
      <Modal visible header={t('Delete Container?')} onDismiss={data.onClose}>
        <Spinner size="big" />
      </Modal>
    );
  }

  if (deleteContainerError) {
    return (
      <Modal visible header={t('Delete Container?')} onDismiss={data.onClose}>
        <Alert data-test-id="errorAlert" type="error">
          {deleteContainerError.message}
        </Alert>
      </Modal>
    );
  }

  if (deleteContainerSuccessResponse) {
    return (
      <Modal
        visible
        header={t('Delete Container?')}
        onDismiss={() => window.location.assign('/assets-inventory/container')}>
        <Alert data-test-id="successAlert" type="success">
          {t(`Container ${data.containerName} deleted successfully`)}
        </Alert>
      </Modal>
    );
  }

  return (
    <Modal
      onDismiss={data.onClose}
      visible={data.showContainerDeleteConfirmModal}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              data-test-id="delete"
              variant="primary"
              disabled={!deleteReasonCode}
              onClick={() => handleDeleteContainer()}>
              {t('Delete Container')}
            </Button>
          </SpaceBetween>
        </Box>
      }
      header="Delete Container?">
      <SpaceBetween direction="vertical" size="m">
        <Alert type="warning">{t('You are deleting this container')}</Alert>
        <TextContent>
          <p>
            <small>{t('You must provide a reason to delete this container')}</small>
          </p>
        </TextContent>
        <Select
          data-test-id="deleteReasonCodeDropdown"
          selectedOption={deleteReasonCode}
          onChange={({ detail }) => {
            setDeleteReasonCode(detail.selectedOption);
          }}
          expandToViewport
          options={data.deleteReasonCodes}
        />
      </SpaceBetween>
    </Modal>
  );
};

export default ContainerDeleteConfirmationModal;
